import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Spinner from '../components/Spinner/spinner';
import { getRiskMetrics, updateRiskMetrics } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import UpdateFundsModal from '../components/modals/UpdateFundsModal';
import ProtectorSection from '../organisms/Protectors/ProtectorSection';

interface RiskMetricsData {
  authorized_risk_fund: number;
  maximum_leverage: number;
  maximum_outstanding_risk: number;
  outstanding_risk: number;
  leverage: number;
  integrated_fund: number;
}

const FundsAndProtectors = () => {
  const navigate = useNavigate();
  const [metricsData, setMetricsData] = useState<RiskMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalField, setModalField] = useState<{
    name: 'authorized_risk_fund' | 'maximum_leverage';
    label: string;
    value: number;
  } | null>(null);

  useEffect(() => {
    fetchRiskMetrics();
  }, []);

  const fetchRiskMetrics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getRiskMetrics();
      if (response.success && 'data' in response) {
        setMetricsData(response.data);
      } else {
        setError('Error al cargar los datos de fondos y protectores');
      }
    } catch (err) {
      setError('Error al cargar los datos de fondos y protectores');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.guarantees);
  };

  const handleOpenModal = (
    fieldName: 'authorized_risk_fund' | 'maximum_leverage',
    fieldLabel: string,
    currentValue: number
  ) => {
    setModalField({ name: fieldName, label: fieldLabel, value: currentValue });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalField(null);
  };

  const handleUpdateField = async (fieldName: string, newValue: number) => {
    await updateRiskMetrics(fieldName, newValue);
    await fetchRiskMetrics();
  };

  const formatNumber = (amount: number | undefined | null) => {
    if (amount === null || amount === undefined) {
      return '0';
    }
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return (
      <Card width='-webkit-fill-available'>
        <Card display='flex' justifyContent='center' alignItems='center' height='60vh'>
          <Spinner />
        </Card>
      </Card>
    );
  }

  if (error || !metricsData) {
    return (
      <Card
        variant='neutral'
        modifier='inverted'
        spacing={'4'}
        height='-webkit-fill-available'
        width='85vw'
      >
        <Card display='flex' justifyContent='space-between'>
          <Card display='flex' alignItems='center'>
            <H withMargin={false} type='1' weight='bold' modifier='strong'>
              Fondos y protectores
            </H>
          </Card>
        </Card>
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            {error || 'No se pudieron cargar los datos'}
          </P>
          <P variant='primary' align='center'>
            Intente nuevamente más tarde.
          </P>
        </Card>
      </Card>
    );
  }

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Fondos y protectores
          </H>
        </Card>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '16px' }}>

        {/* Métricas principales en cards de 3 columnas */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' justifyContent='space-between' alignItems='center'>
              <Card display='flex' alignItems='center' gap='16px'>
                <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                  {formatNumber(metricsData.authorized_risk_fund)}
                </H>
                <H withMargin={false} type='3' weight='bold' modifier='strong'>Fondo de riesgo</H>
              </Card>
              <Icon
                name='edit_outline'
                variant='info'
                width={24}
                height={24}
                cursor='pointer'
                onClick={() =>
                  handleOpenModal(
                    'authorized_risk_fund',
                    'Fondo de riesgo',
                    metricsData.authorized_risk_fund
                  )
                }
              />
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' justifyContent='space-between' alignItems='center'>
              <Card display='flex' alignItems='center' gap='16px'>
                <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                  {formatNumber(metricsData.maximum_leverage)}
                </H>
                <H withMargin={false} type='3' weight='bold' modifier='strong'>Apalancamiento máximo</H>
              </Card>
              <Icon
                name='edit_outline'
                variant='info'
                width={24}
                height={24}
                cursor='pointer'
                onClick={() =>
                  handleOpenModal(
                    'maximum_leverage',
                    'Apalancamiento máximo',
                    metricsData.maximum_leverage
                  )
                }
              />
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' alignItems='center' gap='16px'>
              <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                {formatNumber(metricsData.maximum_outstanding_risk)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Riesgo vivo máximo</H>
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' alignItems='center' gap='16px'>
              <H withMargin={false} type='1' weight='bold' modifier='strong'>
                {formatNumber(metricsData.integrated_fund)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Fondo integrado</H>
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' alignItems='center' gap='16px'>
              <H withMargin={false} type='1' weight='bold' modifier='strong'>
                {formatNumber(metricsData.outstanding_risk)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Riesgo vivo</H>
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' alignItems='center' gap='16px'>
              <H withMargin={false} type='1' weight='bold' modifier='strong'>
                {formatNumber(metricsData.leverage)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Apalancamiento</H>
            </Card>
          </Card>
        </div>

        
      </div>
      <ProtectorSection />
      {modalField && (
        <UpdateFundsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          fieldName={modalField.name}
          fieldLabel={modalField.label}
          currentValue={modalField.value}
          onUpdate={handleUpdateField}
        />
      )}
    </Card>
  );
};

export default FundsAndProtectors;
